import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

type ResponsiveBreakpoint = 'xl' | '2xl'
type ResponsiveFormColumns = 'two' | 'three'
type ResponsiveMetricColumns = 'three' | 'four'

const sidebarVisibilityClassMap: Record<ResponsiveBreakpoint, string> = {
  xl: 'hidden xl:block',
  '2xl': 'hidden 2xl:block',
}

const compactNavigationVisibilityClassMap: Record<ResponsiveBreakpoint, string> = {
  xl: 'xl:hidden',
  '2xl': '2xl:hidden',
}

const sidebarMarginClassMap: Record<ResponsiveBreakpoint, string> = {
  xl: 'xl:ml-80',
  '2xl': '2xl:ml-80',
}

const formColumnsClassMap: Record<ResponsiveFormColumns, string> = {
  two: 'grid gap-4 md:grid-cols-2',
  three: 'grid gap-4 md:grid-cols-2 2xl:grid-cols-3',
}

const metricColumnsClassMap: Record<ResponsiveMetricColumns, string> = {
  three: 'grid gap-3 sm:grid-cols-2 xl:grid-cols-3',
  four: 'grid gap-3 sm:grid-cols-2 xl:grid-cols-4',
}

const metricStripColumnsClassMap: Record<ResponsiveMetricColumns, string> = {
  three: 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3',
  four: 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4',
}

interface ResponsiveSidebarLayoutProps {
  sidebar: ReactNode
  compactNavigation: ReactNode
  children: ReactNode
  className?: string
  contentClassName?: string
  contentInnerClassName?: string
  sidebarBreakpoint?: ResponsiveBreakpoint
}

export function ResponsiveSidebarLayout({
  sidebar,
  compactNavigation,
  children,
  className,
  contentClassName,
  contentInnerClassName,
  sidebarBreakpoint = '2xl',
}: ResponsiveSidebarLayoutProps) {
  return (
    <div className={cn('min-h-[calc(100vh-64px)]', className)}>
      <div className={sidebarVisibilityClassMap[sidebarBreakpoint]}>{sidebar}</div>
      <main
        className={cn(
          'min-h-[calc(100vh-64px)]',
          sidebarMarginClassMap[sidebarBreakpoint],
          contentClassName
        )}
      >
        <div
          className={cn(
            'px-4 pb-8 pt-4 sm:px-6 lg:px-8',
            contentInnerClassName
          )}
        >
          <div className={cn('mb-6', compactNavigationVisibilityClassMap[sidebarBreakpoint])}>
            {compactNavigation}
          </div>
          {children}
        </div>
      </main>
    </div>
  )
}

interface ResponsiveSplitLayoutProps {
  main: ReactNode
  aside?: ReactNode
  className?: string
  mainClassName?: string
  asideClassName?: string
  columnsClassName?: string
  asideBreakpoint?: ResponsiveBreakpoint
}

export function ResponsiveSplitLayout({
  main,
  aside,
  className,
  mainClassName,
  asideClassName,
  columnsClassName = '2xl:grid-cols-[minmax(0,1.05fr)_420px]',
  asideBreakpoint = '2xl',
}: ResponsiveSplitLayoutProps) {
  return (
    <div className={cn('grid items-start gap-5', columnsClassName, className)}>
      <div className={cn('min-w-0', mainClassName)}>{main}</div>
      {aside ? (
        <aside
          className={cn(
            'hidden self-start',
            asideBreakpoint === '2xl'
              ? '2xl:sticky 2xl:top-24 2xl:block'
              : 'xl:sticky xl:top-24 xl:block',
            asideClassName
          )}
        >
          {aside}
        </aside>
      ) : null}
    </div>
  )
}

interface ResponsiveWorkspaceLayoutProps {
  navigation: ReactNode
  content: ReactNode
  className?: string
  navigationClassName?: string
  contentClassName?: string
  columnsClassName?: string
  stickyNavigation?: boolean
}

export function ResponsiveWorkspaceLayout({
  navigation,
  content,
  className,
  navigationClassName,
  contentClassName,
  columnsClassName = 'xl:grid-cols-[minmax(280px,320px)_minmax(0,1fr)] 2xl:grid-cols-[340px_minmax(0,1fr)]',
  stickyNavigation = true,
}: ResponsiveWorkspaceLayoutProps) {
  return (
    <div className={cn('grid gap-6', columnsClassName, className)}>
      <aside
        className={cn(
          'min-w-0',
          stickyNavigation && '2xl:sticky 2xl:top-24 2xl:self-start',
          navigationClassName
        )}
      >
        {navigation}
      </aside>
      <div className={cn('min-w-0 space-y-6', contentClassName)}>{content}</div>
    </div>
  )
}

interface ResponsiveFormGridProps {
  children: ReactNode
  className?: string
  columns?: ResponsiveFormColumns
}

export function ResponsiveFormGrid({
  children,
  className,
  columns = 'three',
}: ResponsiveFormGridProps) {
  return <div className={cn(formColumnsClassMap[columns], className)}>{children}</div>
}

interface ResponsiveMetricGridProps {
  children: ReactNode
  className?: string
  columns?: ResponsiveMetricColumns
}

export function ResponsiveMetricGrid({
  children,
  className,
  columns = 'three',
}: ResponsiveMetricGridProps) {
  return <div className={cn(metricColumnsClassMap[columns], className)}>{children}</div>
}

interface ResponsiveMetricStripProps {
  children: ReactNode
  className?: string
  columns?: ResponsiveMetricColumns
}

export function ResponsiveMetricStrip({
  children,
  className,
  columns = 'three',
}: ResponsiveMetricStripProps) {
  return (
    <div
      className={cn(
        'grid min-w-0 w-full gap-2 sm:gap-3',
        metricStripColumnsClassMap[columns],
        className
      )}
    >
      {children}
    </div>
  )
}
